import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import "./Assets/styles/landing-v2.css";
import darkLogo from "./Assets/images/dark_logo.png";
import flagstoneLogo from "./Assets/images/flagstone_logo.png";

const sectorConfig = {
  voeding: {
    label: "Voeding & drank",
    omzetLabel: { 5: "€1–10M", 25: "€10–50M", 75: "€50–100M", 150: "€100M+" },
    note: "In de voedingssector zijn omstellingen (CIP, reinigingen, allergenenwissels) doorgaans de zwaarste verliespost.",
    verlies: {
      wachten:    { low: 0.008, high: 0.018, label: "Wachten & buffers",       context: "Hygiënevensters en batchovergangen veroorzaken structurele wachttijd." },
      omstelling: { low: 0.015, high: 0.030, label: "Omstellingen & reiniging", context: "CIP-cycli, allergenenwissels en formatwissels zijn tijdsintensief en kostbaar." },
      flow:       { low: 0.008, high: 0.018, label: "Capaciteitsverlies",       context: "Suboptimale lijnbalans of onderbenutte capaciteit tussen stappen." },
      stops:      { low: 0.004, high: 0.010, label: "Kleine stops",             context: "Verstoringen door verpakkingsfouten, vulproblemen of sensorstoringen." },
    },
  },
  machinebouw: {
    label: "Machinebouw",
    omzetLabel: { 5: "€1–10M", 25: "€10–50M", 75: "€50–100M", 150: "€100M+" },
    note: "In machinebouw zijn wachttijd (componenten, goedkeuringen, tooling) en omsteltijd (kleine series, hoge variatie) de dominante verliesposten.",
    verlies: {
      wachten:    { low: 0.012, high: 0.025, label: "Wachten & buffers",       context: "Wachten op onderdelen, goedkeuringen of tooling bij kleine series." },
      omstelling: { low: 0.010, high: 0.022, label: "Omstellingen & variatie",  context: "Hoge productmix en korte series maken omsteltijd relatief zwaar." },
      flow:       { low: 0.007, high: 0.016, label: "Capaciteitsverlies",       context: "Bottlenecks in de flow door ongelijke belasting van werkposten." },
      stops:      { low: 0.004, high: 0.010, label: "Kleine stops",             context: "Micro-stops door toolingproblemen, kwaliteitscontrole of materiaalwissels." },
    },
  },
  farma: {
    label: "Farma / chemie",
    omzetLabel: { 5: "€1–10M", 25: "€10–50M", 75: "€50–100M", 150: "€100M+" },
    note: "In farma en chemie domineren omstellings- en validatiekosten. Elke omstelling vereist strikte documentatie, cleaning en validatiestappen.",
    verlies: {
      wachten:    { low: 0.006, high: 0.015, label: "Wachten & buffers",       context: "Wachttijd door kwaliteitscontroles, quarantaineperiodes en vrijgaveprocedures." },
      omstelling: { low: 0.012, high: 0.025, label: "Omstellingen & validatie", context: "Validatie- en documentatieverplichtingen maken elke omstelling kostbaar." },
      flow:       { low: 0.005, high: 0.012, label: "Capaciteitsverlies",       context: "Strikte procesparameters beperken de flexibiliteit in capaciteitsbenutting." },
      stops:      { low: 0.003, high: 0.008, label: "Kleine stops",             context: "Procesverstoringen met disproportionele impact door compliance-vereisten." },
    },
  },
  overig: {
    label: "Overige productie",
    omzetLabel: { 5: "€1–10M", 25: "€10–50M", 75: "€50–100M", 150: "€100M+" },
    note: "Verliesstructuur varieert sterk per omgeving. Ranges gebaseerd op gangbare OEE-benchmarks voor gemengde productieomgevingen.",
    verlies: {
      wachten:    { low: 0.008, high: 0.020, label: "Wachten & buffers",       context: "Wachttijd door interne logistiek, planning of afhankelijkheden tussen stappen." },
      omstelling: { low: 0.008, high: 0.020, label: "Omstellingen & variatie",  context: "Omsteltijd afhankelijk van productvariatie en seriegrootte." },
      flow:       { low: 0.006, high: 0.015, label: "Capaciteitsverlies",       context: "Beschikbare capaciteit geraakt niet optimaal benut door flow-onbalans." },
      stops:      { low: 0.003, high: 0.009, label: "Kleine stops",             context: "Verborgen verlies door frequente kleine verstoringen." },
    },
  },
};

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const fmt = (v) =>
  v >= 1e6
    ? `${(v / 1e6).toFixed(1).replace(/\.0$/, "")}M`
    : `${Math.round(v / 1000)}k`;

const fmtRange = (low, high) => `${fmt(low)} – ${fmt(high)}`;

function LandingPage() {
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedOmzet, setSelectedOmzet] = useState(null);
  const [selectedVerlies, setSelectedVerlies] = useState(new Set());

  const toggleVerlies = (val) => {
    setSelectedVerlies((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const cfg = selectedSector ? sectorConfig[selectedSector] : null;
  const omzetM = selectedOmzet ? selectedOmzet * 1e6 : null;
  const showResult = !!(selectedSector && selectedOmzet && selectedVerlies.size > 0);

  const { calcLow, calcHigh } = useMemo(() => {
    if (!showResult || !cfg || !omzetM) return { calcLow: 0, calcHigh: 0 };
    let low = 0, high = 0;
    selectedVerlies.forEach((v) => {
      if (cfg.verlies[v]) {
        low += cfg.verlies[v].low;
        high += cfg.verlies[v].high;
      }
    });
    return { calcLow: omzetM * low, calcHigh: omzetM * high };
  }, [showResult, cfg, omzetM, selectedVerlies]);

  const { simTotalLow, simTotalHigh } = useMemo(() => {
    if (!cfg || !omzetM) return { simTotalLow: 0, simTotalHigh: 0 };
    const low = Object.values(cfg.verlies).reduce((s, v) => s + v.low, 0) * omzetM;
    const high = Object.values(cfg.verlies).reduce((s, v) => s + v.high, 0) * omzetM;
    return { simTotalLow: low, simTotalHigh: high };
  }, [cfg, omzetM]);

  const rangeText = showResult ? fmtRange(calcLow, calcHigh) : "—";

  const dynamicCtaHref =
    showResult && cfg
      ? `mailto:info@taplr.app?subject=Analyse besparingspotentieel ${cfg.label}&body=Beste Taplr, wij draaien een omzet van ${cfg.omzetLabel[selectedOmzet]} in de sector ${cfg.label} en willen graag onderzoeken hoe we onze operationele verliezen kunnen reduceren.`
      : "mailto:info@taplr.app?subject=Schatting besparingspotentieel";

  return (
    <div
      style={{
        fontFamily: "Inter, Segoe UI, Arial, sans-serif",
        color: "#07142d",
        background: "#ffffff",
        lineHeight: 1.5,
        margin: 0,
      }}
    >
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #e2e7ef",
        }}
      >
        <div className="lp2-container lp2-nav">
          <div className="lp2-brand">
            <img src={darkLogo} alt="Taplr" className="lp2-brand-logo" />
          </div>
          <nav className="lp2-nav-links">
            <button className="lp2-nav-anchor" onClick={() => scrollTo("problems")}>Herkenbaar?</button>
            <button className="lp2-nav-anchor" onClick={() => scrollTo("production-intelligence")}>Production Intelligence</button>
            <button className="lp2-nav-anchor" onClick={() => scrollTo("case")}>Simulatie</button>
            <button className="lp2-nav-anchor" onClick={() => scrollTo("partner")}>Implementatie</button>
            <a className="lp2-cta lp2-cta-secondary" href="mailto:info@taplr.app?subject=Bel me terug">
              Laat een expert mij contacteren
            </a>
            <Link className="lp2-cta lp2-cta-secondary" to="/login">
              Login
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="lp2-hero">
          <div className="lp2-container lp2-hero-grid">
            <div>
              <div className="lp2-eyebrow">
                <span className="lp2-dot"></span>
                Voor productiebedrijven onder druk van stijgende kosten en internationale concurrentie
              </div>
              <h1 style={{ fontSize: "clamp(38px, 4.5vw, 62px)", lineHeight: 1.05, margin: "0 0 24px", letterSpacing: "-0.045em" }}>
                Stop met gissen. Taplr berekent welke operationele keuzes echt renderen.
              </h1>
              <p className="lp2-lead">
                Koppel live OEE- en fabrieksinformatie aan euro&apos;s en marges. Zo bouwt u waterdichte businesscases voor operationele verbeteringen.
              </p>
              <div className="lp2-hero-actions">
                <a className="lp2-cta lp2-cta-secondary" href="mailto:info@taplr.app?subject=Bel me terug">
                  Laat een expert mij contacteren
                </a>
                <button className="lp2-cta" onClick={() => scrollTo("case")}>Bekijk simulatievoorbeeld</button>
              </div>
            </div>

            <div className="lp2-hero-card" id="hero-calculator" aria-label="Verliesimpact calculator">
              <div className="lp2-calc-label">Wat kost operationeel verlies uw bedrijf?</div>
              <div className="lp2-calc-sub">Indicatieve schatting op basis van sectorbenchmarks</div>

              <div className="lp2-calc-field">
                <label className="lp2-calc-field-label">Sector</label>
                <div className="lp2-calc-options">
                  {[
                    { val: "voeding", label: "Voeding & drank" },
                    { val: "machinebouw", label: "Machinebouw" },
                    { val: "farma", label: "Farma / chemie" },
                    { val: "overig", label: "Overig productie" },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      className={`lp2-calc-opt${selectedSector === opt.val ? " active" : ""}`}
                      aria-pressed={selectedSector === opt.val ? "true" : "false"}
                      onClick={() => setSelectedSector(opt.val)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lp2-calc-field">
                <label className="lp2-calc-field-label">Jaaromzet (productie)</label>
                <div className="lp2-calc-options">
                  {[
                    { val: 5, label: "€1–10M" },
                    { val: 25, label: "€10–50M" },
                    { val: 75, label: "€50–100M" },
                    { val: 150, label: "€100M+" },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      className={`lp2-calc-opt${selectedOmzet === opt.val ? " active" : ""}`}
                      aria-pressed={selectedOmzet === opt.val ? "true" : "false"}
                      onClick={() => setSelectedOmzet(opt.val)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lp2-calc-field">
                <label className="lp2-calc-field-label">
                  Waar verliest u vandaag tijd?{" "}
                  <span style={{ fontWeight: 400, color: "#657084" }}>(meerdere mogelijk)</span>
                </label>
                <div className="lp2-calc-options lp2-flex-wrap">
                  {[
                    { val: "wachten", label: "Wachten & buffers" },
                    { val: "omstelling", label: "Omstellingen" },
                    { val: "flow", label: "Capaciteitsverlies" },
                    { val: "stops", label: "Kleine stops" },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      className={`lp2-calc-opt${selectedVerlies.has(opt.val) ? " active" : ""}`}
                      aria-pressed={selectedVerlies.has(opt.val) ? "true" : "false"}
                      onClick={() => toggleVerlies(opt.val)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {showResult ? (
                <div className="lp2-calc-result">
                  <div className="lp2-calc-result-label">Indicatieve jaarlijkse verliesimpact</div>
                  <div className="lp2-calc-result-value">{rangeText} / jaar</div>
                  <div className="lp2-calc-result-note">{cfg.note}</div>
                  <a className="lp2-cta lp2-cta-dynamic-calc" href={dynamicCtaHref}>
                    Ontdek hoe we deze {rangeText} reduceren &rarr;
                  </a>
                  <div className="lp2-calc-method-note">
                    <span className="lp2-calc-method-icon">&#8505;</span>
                    Berekend als gederfde capaciteitswaarde of doorlopende kost. De exacte berekeningsmethode wordt volledig afgestemd op uw situatie.
                    <a href="#how-we-calculate" className="lp2-calc-method-link">Hoe berekenen we dit?</a>
                  </div>
                </div>
              ) : (
                <div className="lp2-calc-empty">&#8592; Vul uw situatie in voor een indicatie</div>
              )}
            </div>
          </div>
        </section>

        <section id="problems" style={{ padding: "82px 0" }}>
          <div className="lp2-container">
            <div className="lp2-section-title">
              <h2>Veel productiebedrijven beschikken al over data. Maar welke verbeteringen leveren financieel echt verschil op?</h2>
              <p>ERP-, MES- en BI-systemen leveren vandaag enorme hoeveelheden operationele data. Hoe gemakkelijk kunnen onderstaande beslissingen onderbouwd worden?</p>
            </div>
            <div className="lp2-pain-grid">
              {[
                { n: "01", t: "Hoeveel kost een specifiek probleem ons echt?" },
                { n: "02", t: "Waar brengt een verbetering vandaag snel geld op?" },
                { n: "03", t: "Hoeveel mag een technische oplossing kosten?" },
                { n: "04", t: "Welk probleem moet het team eerst aanpakken?" },
                { n: "05", t: "Wat gebeurt er als volumes dalen of kosten stijgen?" },
                { n: "06", t: "Klopt onze productmix en prijssetting nog op de vloer?" },
              ].map((item) => (
                <div key={item.n} className="lp2-pain-card">
                  <div>
                    <div className="lp2-pain-number">{item.n}</div>
                    <h3>{item.t}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="lp2-intelligence" id="production-intelligence">
          <div className="lp2-container">
            <div className="lp2-section-title">
              <h2>Zekerheid voor belangrijke operationele beslissingen</h2>
              <p>
                Veel productiebedrijven proberen vandaag operationele verbeteringen te prioriteren op basis van uren, dashboards en ervaring. Taplr voegt een financiële laag toe bovenop bestaande productiedata zodat direct zichtbaar wordt wat problemen kosten en verbeteringen opbrengen.
              </p>
            </div>
            <div className="lp2-flow">
              {[
                { n: "1", t: "Waar verliezen we vandaag geld?" },
                { n: "2", t: "Welke verliezen worden kritisch bij marktdruk?" },
                { n: "3", t: "Welke investering levert het meeste rendement op?" },
                { n: "4", t: "Welke plants, lijnen of flows verdienen vandaag prioriteit?" },
              ].map((step) => (
                <div key={step.n} className="lp2-flow-step">
                  <span>{step.n}</span>
                  <h3>{step.t}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="case" style={{ padding: "82px 0" }}>
          <div className="lp2-container">
            <div className="lp2-section-title">
              {selectedSector && omzetM && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 999, background: "#fff4e8", color: "#9b5208", fontWeight: 700, fontSize: 13 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f28c1b", display: "inline-block" }}></span>
                    {cfg.label} &middot; {cfg.omzetLabel[selectedOmzet]}
                  </span>
                </div>
              )}
              <h2>
                {selectedSector && omzetM
                  ? `Indicatieve verliesstructuur — ${cfg.label}`
                  : "Indicatief voorbeeld van een productiebedrijf"}
              </h2>
              <p>
                {selectedSector && omzetM
                  ? "Op basis van uw selectie. Alle cijfers zijn indicatief en bedoeld als gespreksaanleiding, niet als definitieve analyse."
                  : "Vul uw sector en omzet bovenaan in voor een indicatieve simulatie. Onderstaande cijfers zijn volledig indicatief."}
              </p>
              {!(selectedSector && omzetM) && (
                <button onClick={() => scrollTo("hero-calculator")} style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 12, padding: "10px 18px", borderRadius: 999, background: "#f28c1b", color: "white", fontWeight: 700, fontSize: 13, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                  &#8593; Vul uw situatie in
                </button>
              )}
            </div>

            <div className="lp2-case-grid">
              <div className="lp2-case-panel lp2-case-panel-dark">
                <h3>Financiële inzichten</h3>
                <div className="lp2-case-kpi">
                  <div className="lp2-mini-kpi">
                    <strong>{omzetM ? (selectedOmzet >= 100 ? "€100M+" : `~€${selectedOmzet}M`) : "—"}</strong>
                    <span>schaal van productieomgeving</span>
                  </div>
                  <div className="lp2-mini-kpi">
                    <strong>{cfg && omzetM ? fmtRange(simTotalLow, simTotalHigh) : "—"}</strong>
                    <span>{cfg && omzetM ? "indicatieve jaarlijkse verliesrange (alle posten samen)" : "indicatieve jaarlijkse verliesrange (alle posten)"}</span>
                  </div>
                  <div className="lp2-mini-kpi">
                    <strong>Scenario-analyse</strong>
                    <span>wat gebeurt er als volumes dalen, kosten stijgen of capaciteit onder druk komt?</span>
                  </div>
                </div>
                {cfg && omzetM && (
                  <p style={{ marginTop: 16, fontSize: 12, opacity: 0.7, lineHeight: 1.5 }}>{cfg.note}</p>
                )}
              </div>

              <div className="lp2-case-panel">
                <h3>Verliesposten naar impact</h3>
                <table className="lp2-loss-table">
                  <thead>
                    <tr>
                      <th>Verliespost</th>
                      <th>Indicatieve range / jaar <span title="Berekend als directe kost of opportuniteitskost, instelbaar per situatie." style={{ cursor: "help", color: "#657084", fontSize: 11 }}>&#9432;</span></th>
                      <th>Typische context</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cfg && omzetM ? (
                      Object.entries(cfg.verlies).map(([key, v]) => {
                        const isSelected = selectedVerlies.has(key);
                        return (
                          <tr key={key} style={isSelected ? { background: "#fffbf5" } : {}}>
                            <td>
                              {v.label}
                              {isSelected && (
                                <span style={{ fontSize: 10, background: "#f28c1b", color: "white", borderRadius: 999, padding: "2px 7px", fontWeight: 700, verticalAlign: "middle", marginLeft: 6 }}>
                                  aangevinkt
                                </span>
                              )}
                            </td>
                            <td><strong>{fmtRange(omzetM * v.low, omzetM * v.high)}</strong></td>
                            <td style={{ fontSize: 12, color: "#657084" }}>{v.context}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={3} style={{ color: "#657084", fontSize: 13, padding: "16px 0" }}>
                          &#8592; Vul sector en omzet in bovenaan voor een op maat geschatte simulatie.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <p style={{ fontSize: 11, color: "#657084", marginTop: 12, lineHeight: 1.5 }}>
                  Indicatief op basis van gangbare OEE-ranges. Werkelijke impact hangt af van volumes, marges, productmix en kostenstructuur.
                </p>
                <details id="how-we-calculate" className="lp2-method-details">
                  <summary className="lp2-method-summary">&#9432; Hoe berekenen we dit?</summary>
                  <div className="lp2-method-body">
                    <p><strong>De berekeningsmethode is instelbaar per situatie.</strong> Taplr hanteert geen vaste, rigide formule, omdat de juiste aanpak afhangt van uw kostenstructuur en strategische doelstellingen.</p>
                    <p>Twee gangbare benaderingen binnen het platform:</p>
                    <ul>
                      <li><strong>Directe kost</strong> — loonkosten, machinekost en vaste lasten die doorlopen tijdens de verliesperiode. Relevant bij hoge vaste operationele kosten.</li>
                      <li><strong>Opportuniteitskost</strong> — wat had de vrijgekomen capaciteit kunnen opbrengen in extra afzet? Gecorreleerd aan brutomarges.</li>
                    </ul>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </section>

        <section id="partner" style={{ padding: "82px 0", background: "#f6f8fb" }}>
          <div className="lp2-container">
            <div className="lp2-section-title">
              <h2>Gebouwd voor productieomgevingen</h2>
              <p>Van batchproductie en voedingsindustrie tot machinebouw, assemblage en omgevingen met hoge vaste kosten of complexe flows.</p>
            </div>
            <div className="lp2-split">
              <div className="lp2-info-card">
                <div className="lp2-logo-word"><img src={darkLogo} alt="Taplr" style={{ height: 48, width: "auto" }} /></div>
                <h3>Production Intelligence-platform</h3>
                <ul className="lp2-bullets">
                  <li>Economische simulaties direct gekoppeld aan productiedata</li>
                  <li>Verborgen en indirecte fabriekskosten zichtbaar maken</li>
                  <li>Investerings-, benchmark- en prioriteitsanalyse voor management</li>
                  <li>Ondersteuning van multi-plant en multi-line businesscases</li>
                </ul>
              </div>
              <div className="lp2-info-card">
                <div className="lp2-logo-word"><img src={flagstoneLogo} alt="Flagstone" style={{ height: 48, width: "auto" }} /></div>
                <h3>OT/IT implementatie- en operationspartner</h3>
                <ul className="lp2-bullets">
                  <li>Naadloze OT/IT integraties, deployment &amp; industriële connectiviteit</li>
                  <li>Diepgaande MES-, shopfloor- en productie-expertise</li>
                  <li>Operationele uitrol en actieve begeleiding op de werkvloer</li>
                  <li>Digitalisering van discrete en batchproductieomgevingen</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="personas" style={{ padding: "82px 0" }}>
          <div className="lp2-container">
            <div className="lp2-section-title">
              <h2>Eén bron van waarheid, van fabrieksvloer tot directiekamer</h2>
              <p>Taplr overbrugt het gat tussen technische prestaties en financiële resultaten, afgestemd op de prioriteiten van elke stakeholder.</p>
            </div>
            <div className="lp2-persona-grid">
              <div className="lp2-persona-card">
                <div className="lp2-persona-role">Voor de Plant &amp; Operations Manager</div>
                <h3 style={{ margin: "0 0 12px", fontSize: 20, letterSpacing: "-0.02em" }}>Prioriteer op ROI, niet op onderbuikgevoel</h3>
                <p style={{ margin: 0, fontSize: 14, color: "#657084", lineHeight: 1.6 }}>Onderbouw uw investeringsaanvragen of onderhoudsplannen met harde financiële scenario&apos;s. Bewijs direct welke optimalisatie de meeste euro&apos;s onder de streep oplevert.</p>
              </div>
              <div className="lp2-persona-card">
                <div className="lp2-persona-role">Voor de CFO &amp; Directie</div>
                <h3 style={{ margin: "0 0 12px", fontSize: 20, letterSpacing: "-0.02em" }}>Krijg grip op de werkelijke kosten van verlies</h3>
                <p style={{ margin: 0, fontSize: 14, color: "#657084", lineHeight: 1.6 }}>Vertaal technische OEE-percentages naar directe impact op uw marges en productmix. Houd grip op doorlopende kosten wanneer volumes schommelen of energietarieven stijgen.</p>
              </div>
              <div className="lp2-persona-card">
                <div className="lp2-persona-role">Voor de IT / OT Manager</div>
                <h3 style={{ margin: "0 0 12px", fontSize: 20, letterSpacing: "-0.02em" }}>Geen extra datasilo, wel maximale waarde</h3>
                <p style={{ margin: 0, fontSize: 14, color: "#657084", lineHeight: 1.6 }}>Taplr integreert naadloos via moderne API&apos;s op uw bestaande SAP, Dynamics, Odoo of MES-omgeving. Geen handmatige exports, veilige architectuur en behoud van uw huidige stack.</p>
              </div>
            </div>
          </div>
        </section>

        <div className="lp2-quote-band">
          <div className="lp2-container lp2-quote">
            <div className="lp2-quote-icon">&ldquo;</div>
            <p style={{ fontSize: "clamp(26px, 3vw, 40px)", lineHeight: 1.18, letterSpacing: "-0.035em", margin: 0, fontWeight: 800 }}>
              Wanneer marges onder druk staan, wordt de verkeerde operationele prioriteit veel duurder.
            </p>
          </div>
        </div>
      </main>

      <footer id="contact" className="lp2-footer">
        <div className="lp2-container lp2-footer-grid">
          <div>
            <h2 style={{ margin: "0 0 12px", fontSize: 40, letterSpacing: "-0.03em" }}>
              Welke operationele problemen kosten vandaag het meeste geld?
            </h2>
            <p style={{ color: "#b9c3d4" }}>
              Bekijk hoe Taplr productiegegevens vertaalt naar economische simulaties, investeringsinzichten en scherpere prioriteiten.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <a className="lp2-cta lp2-cta-secondary" href="mailto:info@taplr.app?subject=Kennismaking Taplr">
              Laat een expert mij contacteren
            </a>
            <a className="lp2-cta" href="mailto:info@taplr.app?subject=Demo aanvraag">
              Vraag een demo aan
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
